// @flow

import * as React from 'react';

import clsx from 'clsx';

import { selectCartIsActive, type ProductId } from '@gandi/cart';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectIsPostingCheckout } from 'src/modules/app/selectors';

import { useBestCatalogPrice } from '../../../modules/catalog';

import { DurationRowBanner } from './DurationRowBanner';
import { DurationRowMultiyearPromo } from './DurationRowMultiyearPromo';
import DurationSelect, {
  type Props as DurationSelectProps,
} from './DurationSelect';
import Price from './Price';

import styles from './DurationRow.scss';

export type Props = {
  disabledOption: boolean,
  showOption?: boolean,
  unit?: DurationSelectProps['unit'],
  value: number,
  onChange?: DurationSelectProps['onChange'],
  values?: Array<number>,
  /** normal (crossed out) price in case of discount */
  priceCrossedOut?: number,
  /** total is total price */
  total: void | number,
  children?: React.Node,
  hasTaxes?: boolean,
  /** productId is used to generate a unique field id */
  productId: ProductId,
  /** @deprecated Just use the `children` prop; it's the same thing. */
  popoverInfo?: React.Node,
};

export default function DurationRow({
  showOption = true,
  value,
  onChange,
  values,
  unit = 'y',
  priceCrossedOut,
  total,
  disabledOption,
  children,
  hasTaxes = false,
  productId,
  popoverInfo,
}: Props): React.MixedElement {
  const currentValues = values ? [...values] : [];
  const active = useAppSelector(selectCartIsActive);
  const isPostingCheckout = useAppSelector(selectIsPostingCheckout);
  const inputId = `duration-${productId}`;

  const { findBetterOffer } = useBestCatalogPrice(productId);
  const { hasBetterOffer, basePercent, hookPercent, promoMinDuration } =
    findBetterOffer();

  // Add value if error
  if (currentValues && !currentValues.includes(value) && value)
    currentValues.push(value);

  const durationWithExtra = (
    <>
      {showOption && (
        <DurationSelect
          id={inputId}
          unit={unit}
          disabled={disabledOption}
          readOnly={!active || isPostingCheckout}
          values={currentValues}
          onChange={onChange}
          value={value}
          productId={productId}
        />
      )}
      {hasBetterOffer && (
        <DurationRowMultiyearPromo
          basePercent={basePercent}
          hasBetterOffer={hasBetterOffer}
          hookPercent={hookPercent}
          promoMinDuration={promoMinDuration}
        />
      )}
      {children}
    </>
  );

  return (
    <div
      className={clsx(
        styles.root,
        hasBetterOffer ? styles.hasBetterOffer : undefined,
      )}
    >
      {hasBetterOffer ? (
        <DurationRowBanner>{durationWithExtra}</DurationRowBanner>
      ) : (
        durationWithExtra
      )}
      {popoverInfo}

      <Price
        price={total}
        priceCrossedOut={priceCrossedOut}
        showExcludeVatMention={hasTaxes}
      />
    </div>
  );
}
